// PlansTab.jsx — 테넌트 관리 '요금제' 탭 (2026-10-25 요금제 4-2, 운영자 전용)
//   요금제 = 테넌트 메뉴 묶음 + 기본 인원 한도 + 월 요금. 만들기·고치기·지우기(쓰는 테넌트가 있으면 서버가 지우기 거절).
//   결정 1-b: 요금제를 고쳐도 쓰는 테넌트 메뉴는 그대로 → 구독 탭에서 테넌트마다 [요금제대로 맞추기].
//   결정 2-a: 업무/기능 배정은 늘 포함(체크 해제 불가).
import React, { useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { input, btn, btnOutline, card, th, td } from './saasShared';
import { hardcodedNote, RED_CARD, RED_NOTE } from './hardcodedMenus';

const won = (n) => `${Number(n).toLocaleString('ko-KR')}원`;
const digits = (v) => String(v ?? '').replace(/[^0-9]/g, '');
const emptyForm = (always) => ({ code: '', name: '', modules: new Set(always), staffLimit: '', monthlyPrice: '', tenantCount: 0 });

function PlanForm({ form, setForm, modules, always, onSave, onCancel, saving }) {
  const toggle = (code) => {
    if (always.has(code)) return;
    const next = new Set(form.modules);
    if (next.has(code)) next.delete(code); else next.add(code);
    setForm({ ...form, modules: next });
  };
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <strong style={{ fontSize: 13 }}>{form.code ? '요금제 고치기' : '새 요금제'}</strong>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>이름
          <input style={{ ...input, width: 180 }} maxLength={40} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>월 요금
          <input
            style={{ ...input, width: 120, textAlign: 'right' }}
            type="text"
            inputMode="numeric"
            placeholder="(선택)"
            value={form.monthlyPrice ? Number(form.monthlyPrice).toLocaleString('ko-KR') : ''}
            onChange={(e) => setForm({ ...form, monthlyPrice: digits(e.target.value) })}
          />원
        </label>
        <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>기본 인원 한도
          <input
            style={{ ...input, width: 90, textAlign: 'right' }}
            type="text"
            inputMode="numeric"
            placeholder="제한 없음"
            value={form.staffLimit}
            onChange={(e) => setForm({ ...form, staffLimit: digits(e.target.value) })}
          />명
        </label>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700 }}>메뉴 <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({form.modules.size}개)</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 6 }}>
        {modules.map((m) => {
          const fixed = always.has(m.code);
          const hcNote = hardcodedNote(m.code); // 하드코딩 메뉴 = 연한 붉은색 + 한 줄 설명(2026-10-27, 운영자 화면)
          const box = hcNote ? { ...RED_CARD, borderRadius: 6, padding: '4px 6px', alignItems: 'flex-start' } : { alignItems: 'center' };
          return (
            <label key={m.code} data-menu-code={m.code} data-hardcoded={hcNote ? 'true' : undefined} style={{ fontSize: 12, display: 'flex', gap: 6, minWidth: 0, color: fixed ? 'var(--text-muted)' : 'var(--text)', ...box }}>
              <input type="checkbox" checked={form.modules.has(m.code)} disabled={fixed} onChange={() => toggle(m.code)} />
              <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}{fixed ? ' (항상 포함)' : ''}</span>
                {hcNote && <span style={RED_NOTE}>{hcNote}</span>}
              </span>
            </label>
          );
        })}
      </div>
      {form.code && form.tenantCount > 0 && (
        <div style={{ fontSize: 11, color: '#b45309', lineHeight: 1.6 }}>
          이 요금제를 쓰는 테넌트 {form.tenantCount}곳의 메뉴는 바뀌지 않아요. 저장 뒤 구독 탭에서 테넌트마다 [요금제대로 맞추기]를 눌러 주세요.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={btn} onClick={onSave} disabled={saving}>저장</button>
        <button style={btnOutline} onClick={onCancel} disabled={saving}>취소</button>
      </div>
    </div>
  );
}

export default function PlansTab({ planInfo, showToast, onChanged }) {
  const plans = planInfo?.plans || [];
  const modules = planInfo?.modules || [];
  const always = new Set(planInfo?.always_modules || ['WORK_ASSIGNMENT']);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const startNew = () => setForm(emptyForm(always));
  const startEdit = (p) => setForm({
    code: p.code,
    name: p.name,
    modules: new Set([...(p.modules || []), ...always]),
    staffLimit: p.staff_limit ? String(p.staff_limit) : '',
    monthlyPrice: p.monthly_price != null ? String(p.monthly_price) : '',
    tenantCount: p.tenant_count || 0,
  });

  async function save() {
    if (!form.name.trim()) { showToast('요금제 이름을 적어 주세요.', 'error'); return; }
    const body = { name: form.name.trim(), modules: [...form.modules], staffLimit: form.staffLimit, monthlyPrice: form.monthlyPrice };
    setSaving(true);
    const res = form.code
      ? await apiClient.put(`/api/admin/saas/plans/${form.code}`, body)
      : await apiClient.post('/api/admin/saas/plans', body);
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '저장 실패', 'error'); return; }
    showToast(form.code ? '요금제를 고쳤어요.' : '요금제를 만들었어요.', 'success');
    setForm(null);
    await onChanged();
  }

  async function remove(p) {
    if (!window.confirm(`'${p.name}' 요금제를 지울까요?`)) return;
    const res = await apiClient.delete(`/api/admin/saas/plans/${p.code}`);
    if (!res.ok) { showToast(res.data?.message || '지우기 실패', 'error'); return; }
    showToast('요금제를 지웠어요.', 'success');
    await onChanged();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        요금제 = 테넌트에 줄 메뉴 묶음 + 기본 인원 한도 + 월 요금. 테넌트에 붙이는 건 구독 탭에서 해요(붙이면 메뉴를 요금제대로 맞춤).
      </div>
      {form
        ? <PlanForm form={form} setForm={setForm} modules={modules} always={always} onSave={save} onCancel={() => setForm(null)} saving={saving} />
        : <div><button style={btn} onClick={startNew}>새 요금제</button></div>}
      <div style={{ ...card, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
          <thead>
            <tr><th style={th}>이름</th><th style={th}>월 요금</th><th style={th}>기본 인원</th><th style={th}>메뉴</th><th style={th}>쓰는 테넌트</th><th style={th}></th></tr>
          </thead>
          <tbody>
            {plans.length === 0 && <tr><td style={{ ...td, color: 'var(--text-muted)' }} colSpan={6}>아직 요금제가 없어요.</td></tr>}
            {plans.map((p) => (
              <tr key={p.code}>
                <td style={{ ...td, fontWeight: 700 }}>{p.name}</td>
                <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{p.monthly_price != null ? won(p.monthly_price) : '-'}</td>
                <td style={td}>{p.staff_limit ? `${p.staff_limit}명` : '제한 없음'}</td>
                <td style={{ ...td, fontSize: 12, lineHeight: 1.5, maxWidth: 360 }}>
                  <strong>{(p.modules || []).length}개</strong>
                  <span style={{ color: 'var(--text-muted)' }}> · {(p.module_names || []).join(', ')}</span>
                </td>
                <td style={td}>{p.tenant_count}곳</td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button style={btnOutline} onClick={() => startEdit(p)}>고치기</button>
                    <button style={btnOutline} onClick={() => remove(p)}>지우기</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
